import { z } from 'zod';
import {
  ACCREDITATION,
  CRM_ACTIVITY_TYPES,
  CRM_CONTACT_TYPES,
  CRM_PIPELINE_STATUSES,
  DEAL_STATUSES,
  ROLES,
} from '../constants.js';

export const uuidSchema = z.string().uuid();

export const updateProfileSchema = z.object({
  full_name: z.union([z.string().min(1).max(200), z.null()]).optional(),
  accreditation_status: z
    .enum([
      ACCREDITATION.ACCREDITED,
      ACCREDITATION.NON_ACCREDITED,
      ACCREDITATION.UNKNOWN,
    ])
    .optional(),
  avatar_url: z.union([z.string().url().max(2000), z.null()]).optional(),
});

export const createDealSchema = z.object({
  title: z.string().min(1).max(500),
  description: z.string().min(1).max(50000),
  irr: z.number().min(0).max(100).nullable().optional(),
  equity_multiple: z.number().min(0).max(50).nullable().optional(),
  min_investment: z.number().int().min(1000),
  status: z
    .enum([
      DEAL_STATUSES.DRAFT,
      DEAL_STATUSES.OPEN,
      DEAL_STATUSES.CLOSED,
      DEAL_STATUSES.FUNDED,
    ])
    .default(DEAL_STATUSES.DRAFT),
  image_paths: z.array(z.string().max(2000)).max(20).optional(),
  property_type: z.string().max(200).nullable().optional(),
  property_class: z.string().max(200).nullable().optional(),
  strategy: z.string().max(500).nullable().optional(),
  city: z.string().max(200).nullable().optional(),
  state: z.string().max(80).nullable().optional(),
  featured_on_landing: z.boolean().optional(),
  archived: z.boolean().optional(),
});

export const updateDealSchema = createDealSchema.partial();

export const createInvestmentSchema = z.object({
  deal_id: z.string().uuid(),
  amount: z.number().int().positive(),
});

export const investPaymentIntentSchema = z.object({
  investment_id: z.string().uuid(),
});

export const documentRegisterSchema = z.object({
  deal_id: uuidSchema.optional().nullable(),
  user_id: uuidSchema.optional().nullable(),
  storage_path: z.string().min(1).max(2000),
  title: z.string().min(1).max(500),
});

export const approveInvestorSchema = z.object({
  user_id: z.string().uuid(),
  approved: z.boolean(),
});

export const roleMetadataSchema = z.enum([
  ROLES.INVESTOR,
  ROLES.STAFF,
  ROLES.ADMIN,
]);

export const crmNoteSchema = z.object({
  user_id: z.string().uuid(),
  body: z.string().min(1).max(10000),
});

const CRM_TYPE_ENUM = [
  CRM_CONTACT_TYPES.INVESTOR,
  CRM_CONTACT_TYPES.BROKER,
  CRM_CONTACT_TYPES.OWNER,
  CRM_CONTACT_TYPES.LEAD,
] as const;

const CRM_STATUS_ENUM = [
  CRM_PIPELINE_STATUSES.NEW,
  CRM_PIPELINE_STATUSES.CONTACTED,
  CRM_PIPELINE_STATUSES.ENGAGED,
  CRM_PIPELINE_STATUSES.QUALIFIED,
  CRM_PIPELINE_STATUSES.CONVERTED,
  CRM_PIPELINE_STATUSES.LOST,
] as const;

const LEAD_SOURCE_ENUM = ['ira_401k', 'contact', 'generic', 'event_rsvp', 'book_401k'] as const;

/** Create manual CRM record (investor/leads/brokers/sellers). */
export const createCrmContactSchema = z.object({
  full_name: z.string().min(1).max(500),
  email: z.union([z.string().email(), z.literal(''), z.null()]).optional(),
  /** Optional phone — sanitize server-side via nullable helper */
  phone: z.union([z.string().max(50), z.literal(''), z.null()]).optional(),
  desired_commitment: z.union([z.string().max(500), z.literal(''), z.null()]).optional(),
  accredited_investor: z.boolean().optional().nullable(),
  type: z.enum(CRM_TYPE_ENUM),
  status: z.enum(CRM_STATUS_ENUM).default(CRM_PIPELINE_STATUSES.NEW),
  tags: z.array(z.string().max(80)).max(40).optional(),
  notes: z.string().max(200000).optional().nullable(),
  lead_source: z.string().max(200).optional().nullable(),
  assigned_to: uuidSchema.optional().nullable(),
  /** Link to platform investor row when applicable */
  user_id: uuidSchema.optional().nullable(),
});

export const updateCrmContactSchema = createCrmContactSchema.partial();

export const createCrmActivitySchema = z.object({
  contact_id: z.string().uuid(),
  type: z.enum([
    CRM_ACTIVITY_TYPES.CALL,
    CRM_ACTIVITY_TYPES.EMAIL,
    CRM_ACTIVITY_TYPES.MEETING,
    CRM_ACTIVITY_TYPES.NOTE,
  ]),
  content: z.string().min(1).max(50000),
});

/** Path-style activity create omits redundant `contact_id` (from URL segment). */
export const crmActivityBodySchema = createCrmActivitySchema.omit({ contact_id: true });

export const crmLinkDealSchema = z.object({
  deal_id: z.string().uuid(),
});

export const createCrmPipelineDealSchema = z.object({
  name: z.string().min(1).max(500),
  notes: z.string().max(50000).optional().nullable(),
  platform_deal_id: uuidSchema.optional().nullable(),
  underwriting: z.record(z.string(), z.unknown()).optional(),
});

export const updateCrmPipelineDealSchema = createCrmPipelineDealSchema.partial();

export const crmPipelineDealSnapshotSchema = z.object({
  label: z.string().min(1).max(200),
});

export const publicLeadSchema = z
  .object({
    source: z.enum(['ira_401k', 'contact', 'generic', 'book_401k']),
    full_name: z.string().min(1).max(300).optional(),
    email: z.string().email().max(320),
    phone: z.string().max(80).optional(),
    message: z.string().max(10000).optional(),
    meta: z.record(z.string(), z.unknown()).optional(),
  })
  .superRefine((d, ctx) => {
    if (d.source !== 'book_401k') return;
    if (!d.full_name?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'full_name is required for book download',
        path: ['full_name'],
      });
    }
    if (!d.phone?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'phone is required for book download',
        path: ['phone'],
      });
    }
  });

/** Staff manual capture — same fields as public plus internal tags/notes and full source enum */
export const createStaffLeadSchema = z.object({
  source: z.enum(LEAD_SOURCE_ENUM),
  full_name: z.string().max(300).optional().nullable(),
  email: z.string().email().max(320),
  phone: z.string().max(80).optional().nullable(),
  message: z.string().max(10000).optional().nullable(),
  meta: z.record(z.string(), z.unknown()).optional(),
  tags: z.array(z.string().min(1).max(80)).max(40).optional(),
  staff_notes: z.string().max(20000).optional().nullable(),
  /** When source is event RSVP — stored on `meta.ticket_code` (normalized uppercase; min 3 chars when non-empty) */
  ticket_code: z.string().max(80).optional().nullable(),
  event_title: z.string().max(500).optional().nullable(),
  /** ISO 8601 timestamp for `meta.starts_at` */
  event_starts_at: z.string().max(64).optional().nullable(),
  event_is_online: z.boolean().optional().nullable(),
});

/** Staff: move capture lead across marketing pipeline lanes, or edit tags */
export const updateStaffLeadSchema = z
  .object({
    pipeline_status: z.enum(CRM_STATUS_ENUM).optional(),
    tags: z.array(z.string().min(1).max(80)).max(40).optional(),
    staff_notes: z.string().max(20000).optional().nullable(),
    full_name: z.string().max(300).optional().nullable(),
    email: z.string().email().max(320).optional(),
    phone: z.string().max(80).optional().nullable(),
    message: z.string().max(10000).optional().nullable(),
    source: z.enum(LEAD_SOURCE_ENUM).optional(),
    ticket_code: z.string().max(80).optional().nullable(),
    event_title: z.string().max(500).optional().nullable(),
    event_starts_at: z.string().max(64).optional().nullable(),
    event_is_online: z.boolean().optional().nullable(),
  })
  .refine(
    (d) =>
      d.pipeline_status !== undefined ||
      d.tags !== undefined ||
      d.staff_notes !== undefined ||
      d.full_name !== undefined ||
      d.email !== undefined ||
      d.phone !== undefined ||
      d.message !== undefined ||
      d.source !== undefined ||
      d.ticket_code !== undefined ||
      d.event_title !== undefined ||
      d.event_starts_at !== undefined ||
      d.event_is_online !== undefined,
    {
      message:
        'Provide at least one field: pipeline_status, tags, staff_notes, identity/message/source, or event meta (ticket, title, starts_at, is_online)',
    },
  );

export const createMarketingEventSchema = z.object({
  title: z.string().min(1).max(500),
  description: z.string().max(50000).default(''),
  starts_at: z.string().min(1),
  ends_at: z.string().min(1).nullable().optional(),
  time_label: z.string().max(200).nullable().optional(),
  venue: z.string().max(500).default(''),
  is_online: z.boolean().default(false),
  event_url: z.string().url().max(2000).nullable().optional(),
  image_path: z.string().max(2000).nullable().optional(),
  published: z.boolean().default(true),
  timezone: z.string().max(80).default('America/New_York'),
});

export const updateMarketingEventSchema = createMarketingEventSchema.partial();

export const eventRsvpSchema = z.object({
  email: z.string().email().max(320),
  full_name: z.string().min(1).max(300),
  phone: z.string().max(80).optional(),
});

export const eventCheckInSchema = z.object({
  ticket_code: z.string().min(3).max(80).optional(),
  rsvp_id: z.string().uuid().optional(),
}).refine((d) => Boolean(d.ticket_code || d.rsvp_id), { message: 'ticket_code or rsvp_id required' });

export const createResourceArticleSchema = z.object({
  slug: z.string().min(1).max(200).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  title: z.string().min(1).max(500),
  description: z.string().max(2000).default(''),
  category: z.string().min(1).max(80),
  difficulty: z.enum(['beginner', 'intermediate', 'advanced']).default('beginner'),
  read_minutes: z.number().int().min(1).max(240).default(5),
  published_at: z.string().max(32).optional(),
  tags: z.array(z.string().max(80)).max(40).optional(),
  body_md: z.string().max(200000).default(''),
  published: z.boolean().default(true),
  archived: z.boolean().default(false),
  sort_order: z.number().int().optional(),
});

export const updateResourceArticleSchema = createResourceArticleSchema.partial();

const underwritingPair = z.object({
  label: z.string().max(200),
  value: z.string().max(500),
});

/** Payload for site_content.get_started — mirrors GetStarted spotlight */
export const getStartedContentSchema = z.object({
  heroLocationTag: z.string().max(200).optional(),
  heroTitleLine1: z.string().max(300).optional(),
  heroTitleAccent: z.string().max(120).optional(),
  heroTitleLine2: z.string().max(300).optional(),
  heroSubtitle: z.string().max(500).optional(),
  minClassA: z.string().max(80).optional(),
  minClassB: z.string().max(80).optional(),
  linkedDealId: z.string().uuid().nullable().optional(),
  spotlightTitle: z.string().max(500).optional(),
  spotlightSubtitle: z.string().max(500).optional(),
  spotlightImagePath: z.string().max(2000).optional(),
  dealClass: z.string().max(200).optional(),
  strategy: z.string().max(500).optional(),
  units: z.number().int().min(0).optional(),
  locationRanking: z.string().max(500).optional(),
  populationGrowth5Yr: z.string().max(80).optional(),
  underwritingProjectSummary: z.array(underwritingPair).optional(),
  underwritingKeyAssumptions: z.array(underwritingPair).optional(),
  underwritingFootnote: z.string().max(1000).optional(),
});

export const patchSiteContentSchema = z.object({
  value: z.record(z.string(), z.unknown()),
});

const dripWorkflowKindSchema = z.enum([
  'trigger',
  'email',
  'sms',
  'call',
  'webhook',
  'delay',
  'wait',
  'condition',
  'branch',
  'goal',
  'task',
  'tag',
  'event_reminder',
  'confirm_attendance',
  'instagram',
  'facebook',
  'twitter',
]);

const dripEmailConfigSchema = z.object({
  subject: z.string().max(500).optional(),
  bodyPlain: z.string().max(100000).optional(),
  bodyHtml: z.string().max(500000).optional(),
  contentMode: z.enum(['plain', 'html']).optional(),
});

const dripSmsConfigSchema = z.object({
  body: z.string().max(3200).optional(),
});

export const dripWorkflowNodeInputSchema = z.object({
  id: z.string().uuid().optional(),
  kind: dripWorkflowKindSchema,
  title: z.string().min(1).max(200),
  subtitle: z.string().max(4000).optional(),
  triggerKey: z.string().max(120).optional(),
  email: dripEmailConfigSchema.optional(),
  sms: dripSmsConfigSchema.optional(),
});

export const upsertDripTemplateSchema = z
  .object({
    name: z.string().min(1).max(200),
    campaignType: z.enum([
      'lead_nurture',
      'reminders',
      'sales_follow_ups',
      'onboarding',
      're_engagement',
      'event_campaigns',
    ]),
    templateNotes: z.string().max(4000).optional(),
    workflow: z.array(dripWorkflowNodeInputSchema).min(1),
    audience: z.enum(['lead', 'everyone', 'source', 'tag']).default('everyone'),
    audienceSource: z.enum(['ira_401k', 'contact', 'generic', 'event_rsvp', 'book_401k']).optional().nullable(),
    audienceTag: z.string().min(1).max(80).optional().nullable(),
  })
  .superRefine((d, ctx) => {
    if (d.audience === 'source' && !d.audienceSource) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Pick a source when audience is Source',
        path: ['audienceSource'],
      });
    }
    if (d.audience === 'tag' && !(d.audienceTag && d.audienceTag.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter a tag when audience is Tag',
        path: ['audienceTag'],
      });
    }
  });

export const createDripMessageTemplateSchema = z.object({
  kind: z.enum(['email', 'sms']),
  name: z.string().min(1).max(200),
  subject: z.string().max(500).optional().nullable(),
  bodyPlain: z.string().max(100000).optional().nullable(),
  bodyHtml: z.string().max(500000).optional().nullable(),
  contentMode: z.enum(['plain', 'html']).optional(),
});

export const patchDripMessageTemplateSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  subject: z.string().max(500).optional().nullable(),
  bodyPlain: z.string().max(100000).optional().nullable(),
  bodyHtml: z.string().max(500000).optional().nullable(),
  contentMode: z.enum(['plain', 'html']).optional(),
});

/** Drag-and-drop Kanban lane change */
export const crmPipelineMoveSchema = z.object({
  status: z.enum(CRM_STATUS_ENUM),
});

export type CreateCrmContactInput = z.infer<typeof createCrmContactSchema>;
export type UpdateCrmContactInput = z.infer<typeof updateCrmContactSchema>;

export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;
export type CreateDealInput = z.infer<typeof createDealSchema>;
export type UpdateDealInput = z.infer<typeof updateDealSchema>;
export type CreateInvestmentInput = z.infer<typeof createInvestmentSchema>;
export type CreateCrmPipelineDealInput = z.infer<typeof createCrmPipelineDealSchema>;
export type UpdateCrmPipelineDealInput = z.infer<typeof updateCrmPipelineDealSchema>;
export type PublicLeadInput = z.infer<typeof publicLeadSchema>;
export type CreateStaffLeadInput = z.infer<typeof createStaffLeadSchema>;
export type UpdateStaffLeadInput = z.infer<typeof updateStaffLeadSchema>;
export type CreateMarketingEventInput = z.infer<typeof createMarketingEventSchema>;
export type UpdateMarketingEventInput = z.infer<typeof updateMarketingEventSchema>;
export type EventRsvpInput = z.infer<typeof eventRsvpSchema>;
export type EventCheckInInput = z.infer<typeof eventCheckInSchema>;
export type CreateResourceArticleInput = z.infer<typeof createResourceArticleSchema>;
export type UpdateResourceArticleInput = z.infer<typeof updateResourceArticleSchema>;
export type GetStartedContentInput = z.infer<typeof getStartedContentSchema>;
export type PatchSiteContentInput = z.infer<typeof patchSiteContentSchema>;
export type UpsertDripTemplateInput = z.infer<typeof upsertDripTemplateSchema>;
export type CreateDripMessageTemplateInput = z.infer<typeof createDripMessageTemplateSchema>;
export type PatchDripMessageTemplateInput = z.infer<typeof patchDripMessageTemplateSchema>;
